/** Projects hooks. API data only when authenticated (errors → isError + empty). */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PROJECTS_QUERY_PREFIX, TASKS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Project, ProjectStatus } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { projectsApi } from "./projects.api";
import { toProject, toProjectWriteBody, type ProjectInput } from "./projects.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

/** Server-side filters of the projects table (spec §1 — recherche + statut + client). */
export interface ProjectsFilters {
  search?: string;
  status?: string;
  clientId?: string;
}

export type DataSource = "api" | "mock";

export function useProjects(filters: ProjectsFilters = {}): {
  projects: Project[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
} {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const { search, status: statusFilter, clientId } = filters;

  const params = useMemo(
    () => ({
      ...LIST_PARAMS,
      ...(search ? { search } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(clientId ? { clientId } : {}),
    }),
    [search, statusFilter, clientId],
  );

  const query = useQuery({
    queryKey: queryKeys.projectsList(params),
    queryFn: () => projectsApi.list(params),
    enabled: authed,
  });

  if (query.data)
    return {
      projects: query.data.data.map(toProject),
      isLoading: false,
      isError: false,
      source: "api",
      refetch: query.refetch,
    };
  if (query.isError)
    return { projects: [], isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return {
    projects: [],
    isLoading: query.isLoading,
    isError: false,
    source: "api",
    refetch: query.refetch,
  };
}

/**
 * Détail projet (spec §1) — 5 sections : informations générales, tâches,
 * membres associés, activité récente et commentaires. Aucun devis/facture.
 */
export function useProject(id: string | undefined): {
  project?: Project;
  isLoading: boolean;
  isError: boolean;
} {
  const status = useAuthStore((s) => s.status);
  const query = useQuery({
    // Sous le préfixe ["projects"] pour profiter de l'invalidation globale.
    queryKey: ["projects", "detail", id] as const,
    queryFn: () => projectsApi.get(id ?? ""),
    enabled: status === "authenticated" && !!id,
  });
  return {
    project: query.data ? toProject(query.data) : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

function useInvalidateProjects() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: PROJECTS_QUERY_PREFIX }),
      // archiving a project archives its tasks
      qc.invalidateQueries({ queryKey: TASKS_QUERY_PREFIX }),
    ]);
}

export function useCreateProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (input: ProjectInput) => {
      return projectsApi.create(toProjectWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useUpdateProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ProjectInput }) => {
      return projectsApi.update(id, toProjectWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useSetProjectStatus() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ProjectStatus }) => {
      return projectsApi.setStatus(id, status);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useAddProjectComment() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: string }) => {
      return projectsApi.addComment(id, body);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useArchiveProject() {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (id: string) => {
      return projectsApi.archive(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
